import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  InputBase,
  IconButton,
  Chip,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";

const VendorInvoices = () => {
  // Sample data for invoices
  const invoices = [
    {
      id: "INV-2024-0001",
      dueIn: "14 days",
      amount: "482721.30",
      date: "Mar 11, 2024",
      status: "Pending"
    },
    {
      id: "INV-2024-0002",
      dueIn: "20 days",
      amount: "572712.17",
      date: "Mar 12, 2024",
      status: "Pending"
    },
    {
      id: "INV-2024-0003",
      dueIn: "12 days",
      amount: "879311.47",
      date: "Mar 13, 2024",
      status: "Pending"
    },
    {
      id: "INV-2024-0004",
      dueIn: "0 days",
      amount: "353900.78",
      date: "Mar 14, 2024",
      status: "Pending"
    },
    {
      id: "INV-2024-0005",
      dueIn: "25 days",
      amount: "144500.38",
      date: "Mar 15, 2024",
      status: "Pending"
    },
    {
      id: "INV-2024-0006",
      dueIn: "11 days",
      amount: "46900.32",
      date: "Mar 16, 2024",
      status: "Pending"
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case "Paid":
        return { bg: "#DCFCE7", color: "#10B981" };
      case "Pending":
        return { bg: "#FEF3C7", color: "#F59E0B" };
      case "Overdue":
        return { bg: "#FEE2E2", color: "#EF4444" };
      default:
        return { bg: "#F3F4F6", color: "#6B7280" };
    }
  };

  return (
    <div className="page-container">
      <Card
        className="shadow-sm"
        variant="outlined"
        sx={{
          border: "1px solid lightgray",
          borderRadius: "10px",
          width: "100%",
        }}
      >
        <CardHeader
          title={<Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px', color: '#333' }} className="font-rubik">Invoices</Typography>}
          action={
            <div>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  marginRight: "8px",
                  backgroundColor: "#050092",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#050092" },
                  fontWeight: 400,
                }}
                className="font-rubik"
              >
                New Order
              </Button>
            </div>
          }
          sx={{
            margin: "15px",
            display: "flex",
            justifyContent: "space-between",
          }}
        />
        <Divider sx={{ backgroundColor: "darkgray" }} />

        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
            >
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: 250,
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  boxShadow: "none",
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search invoices..."
                  inputProps={{ "aria-label": "search invoices" }}
                />
                <IconButton
                  type="submit"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
              </Paper>

              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                sx={{
                  borderColor: "#e0e0e0",
                  color: "#6B7280",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Filter
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              sx={{
                bgcolor: "#050092",
                "&:hover": { bgcolor: "#050092" },
                borderRadius: "8px",
                textTransform: "none",
                display: { xs: "none", sm: "flex" },
                fontWeight: 400,
              }}
              className="font-rubik"
            >
              Upload Invoice
            </Button>
          </Box>

          <Grid container spacing={3}>
            {invoices.map((invoice) => {
              const statusStyle = getStatusColor(invoice.status);
              return (
                <Grid item xs={12} sm={6} md={4} key={invoice.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "8px",
                      height: "100%",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "medium", color: "#111827", fontFamily: 'Rubik' }}
                      >
                        {invoice.id}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          sx={{ color: "#6B7280", padding: "2px" }}
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: "#6B7280", padding: "2px" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: "#6B7280", mb: 2, fontFamily: 'Rubik' }}
                    >
                      Due in {invoice.dueIn}
                    </Typography>

                    <Typography
                       sx={{ fontWeight: "bold", color: "#111827", my: 1, fontSize:"22px" }} className="font-montserrat"
                    >
                      {invoice.amount}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          mr: 1,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: "#E0E7FF",
                            mr: 0.5,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#6B7280", fontFamily: 'Rubik' }}
                      >
                        {invoice.date}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 'auto',
                        pt: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ mr: 2, fontFamily: 'Rubik' }}>
                        Status
                      </Typography>
                      <Chip
                        label={invoice.status}
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: "medium",
                          borderRadius: "16px",
                          fontSize: "0.75rem",
                          height: "24px",
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorInvoices;
